import React from 'react';
import './DispatchBoard.scss';
import cls from 'classnames';
import { Droppable } from '@hello-pangea/dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToolbox, faTruck } from '@fortawesome/free-solid-svg-icons';
import EquipmentAssignmentCard from './EquipmentAssignmentCard';
import { DraggableResourceTypes } from './Dispatch';

export default class EquipmentJobAssignment extends React.Component {
    getStatusName = () => this.props.requiredEquipmentType?.assignment ? this.props.requiredEquipmentType?.assignment?.confirmationStatusName
        .replace(' ', '')
        .toLowerCase() : '';

    // support keyboard "click"
    handleKeyDown = (e, dropId, ids) => {
        if (e.key === 'Enter' || e.key === ' ') {
            this.props.onSlotSelect('equipment', ids, dropId);
        }
    };

    handleMobileClick = () => {
        const {
            onMobileAssignmentClick,
            job,
            day,
            requiredEquipmentType,
            assignmentIndex,
            dayAssignments,
            getEligibleStaffForEquipment,
            getEligibleEquipmentForEquipment,
        } = this.props;
        const dayInt = day.getDay();

        if (onMobileAssignmentClick && !requiredEquipmentType.assignment) {
            // On mobile, the assignable quantities are in requiredEquipment[0]
            const requiredEquipmentConfig = requiredEquipmentType.requiredEquipment?.[0];
            const equipmentType = requiredEquipmentConfig || { isVehicle: requiredEquipmentType.isVehicle };
            const equipmentBeingAssigned = { equipmentType };

            // Determine what this equipment can be assigned to based on the slot's configuration
            const canAssignToStaff = requiredEquipmentConfig?.employeeAssignableQuantity != null
                && requiredEquipmentConfig.employeeAssignableQuantity > 0;
            const canAssignToEquipment = requiredEquipmentConfig?.equipmentAssignableQuantity != null
                && requiredEquipmentConfig.equipmentAssignableQuantity > 0;

            // Get eligible resources - prioritize equipment over staff if both are allowed
            // Only show ONE type in the dropdown, never both
            const resourceType = canAssignToEquipment ? DraggableResourceTypes.Equipment : DraggableResourceTypes.Staff;
            let eligibleResources = [];

            if (canAssignToEquipment && getEligibleEquipmentForEquipment) {
                // Prioritize equipment assignment (e.g., arrow board to vehicle)
                eligibleResources = getEligibleEquipmentForEquipment(
                    equipmentBeingAssigned,
                    equipmentType,
                    dayAssignments,
                );
            } else if (canAssignToStaff && getEligibleStaffForEquipment) {
                // Fall back to staff assignment if equipment assignment not allowed
                eligibleResources = getEligibleStaffForEquipment(
                    equipmentBeingAssigned,
                    equipmentType,
                    dayAssignments,
                );
            }

            // Create a slot object with the information needed for assignment
            const slot = {
                resourceId: requiredEquipmentType.resourceId,
                title: requiredEquipmentType.description || 'Equipment Assignment',
                isRentalPickup: requiredEquipmentType.isRentalPickup || false,
                requiredEquipment: requiredEquipmentType.requiredEquipment || [],
                equipmentType,
                eligibleResources,
                resourceType,
                eligibleStaff: resourceType === DraggableResourceTypes.Staff ? eligibleResources : [],
                eligibleEquipment: resourceType === DraggableResourceTypes.Equipment ? eligibleResources : [],
            };
            onMobileAssignmentClick(DraggableResourceTypes.Equipment, job, dayInt, slot, assignmentIndex);
        }
    };

    render() {
        const {
            job,
            day,
            requiredEquipmentType,
            dayAssignments,
            assignmentIndex,
            isDropZoneValid,
            dragging,
            onSlotSelect,
            pulsingSlotId,
            dispatchEditPermission,
        } = { ...this.props };

        const dropId = `equipment_${job.id}_${day.getDay()}_${assignmentIndex}`;
        const hasAssignment = !!requiredEquipmentType.assignment;
        const canDrop =
            isDropZoneValid(
                DraggableResourceTypes.Equipment,
                requiredEquipmentType.description,
            ) && !hasAssignment;

        const ids = requiredEquipmentType.requiredEquipment.map((g) => g.id);

        return (
            <div
                key={requiredEquipmentType.assignmentId}
                className="jobAssignment"
            >
                <Droppable
                    key={dropId}
                    droppableId={dropId}
                    isDropDisabled={!canDrop || !dispatchEditPermission}
                    style={{ transform: 'none' }}
                >
                    {(provided, snapshot) => (
                        <div
                            className="assignment-placeholder-row"
                            style={{ height: '50px !important' }}
                        >
                            <div
                                className={cls('jobAssignmentDropTarget', this.getStatusName(), {
                                    dragging: snapshot.isDraggingOver,
                                    show: !hasAssignment || snapshot.isDraggingOver,
                                    invalidDropZone: !canDrop && dragging,
                                    occupied: hasAssignment,
                                    pulse: !hasAssignment && dropId === pulsingSlotId,
                                })}
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                role={!hasAssignment ? 'button' : undefined}
                                tabIndex={!hasAssignment ? 0 : undefined}
                                onClick={() => {
                                    if (!hasAssignment) {
                                        onSlotSelect('equipment', ids, dropId);
                                        this.handleMobileClick();
                                    }
                                }}
                                onKeyDown={(e) =>
                                    this.handleKeyDown(e, dropId, ids)
                                }
                            >
                                {hasAssignment ? (
                                    <EquipmentAssignmentCard
                                        removable
                                        compact
                                        innerRef={provided.innerRef}
                                        provided={provided}
                                        job={job}
                                        day={day}
                                        dayAssignments={dayAssignments}
                                        assignmentIndex={assignmentIndex}
                                        assignmentRequirements={requiredEquipmentType}
                                        requiredEquipment={requiredEquipmentType.assignment ?? null}
                                        onEquipmentAssignmentChange={this.props.onEquipmentAssignmentChange}
                                        onRemoveAssignment={this.props.onRemoveAssignment}
                                        dispatchEditPermission={dispatchEditPermission}
                                    />
                                ) : (
                                    <div className="dropPlaceholderWrapper">
                                        <label className="dropPlaceholder">
                                            <span
                                                style={{
                                                    fontSize: '9px',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={requiredEquipmentType.isVehicle ? faTruck : faToolbox}
                                                    className="mr-1"
                                                />
                                            </span>

                                            {requiredEquipmentType.isRental ? (
                                                <>
                                                    {requiredEquipmentType.isGeneric ? (
                                                        <>
                                                            {requiredEquipmentType.description}
                                                            {' '}


                                                            {requiredEquipmentType.rentalDropoffQuantity > 0 && `(Dropoff x ${requiredEquipmentType.rentalDropoffQuantity}) `}
                                                            {requiredEquipmentType.rentalPickupQuantity > 0 && `(Pickup x ${requiredEquipmentType.rentalPickupQuantity}) `}

                                                        </>
                                                    ) : (
                                                        <>
                                                            {requiredEquipmentType.description}
                                                            {' '}

                                                            {requiredEquipmentType.isRentalPickup ? '(Pickup)' : '(Dropoff)'}
                                                        </>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {requiredEquipmentType.quantity >= 1 && requiredEquipmentType.isGeneric
                                                        ? `${requiredEquipmentType.description} x ${requiredEquipmentType.quantity}`
                                                        : requiredEquipmentType.description
                                                    }
                                                </>
                                            )}
                                        </label>
                                    </div>
                                )}

                                {provided.placeholder}
                            </div>
                        </div>
                    )}
                </Droppable>
            </div>
        );
    }
}
